import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { Organization } from '@/types/settings-api';

/**
 * GET /api/Organization/{organizationId} — current org detail.
 * Powers the Account Details banner (PROD-755) and Contact Details (PROD-756).
 */
export function useOrganization(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: ['settings', 'organization', organizationId],
    queryFn: async ({ signal }) => {
      if (!organizationId) throw new Error('useOrganization called without id');
      // Backend ships the account number as snake_case `acc_No`; the rest of
      // the payload is camelCase. Flatten it to `accNo` on read so consumers
      // (e.g. the Settings header ACCOUNT ID banner) see it — mirrors Flutter
      // `organization_model.dart:96` (`accNo: json["acc_No"]`). Without this
      // the camelCase `org.accNo` is always undefined and renders "—".
      const raw = await get<Organization & { acc_No?: string | null }>(
        `/api/Organization/${organizationId}`,
        { signal },
      );
      return { ...raw, accNo: raw.accNo ?? raw.acc_No ?? null };
    },
    enabled: !!organizationId,
  });
}
